import React, { useState } from "react";
import CabIcon from "../CabIcon";
import { useCabinet } from "../../../context/CabinetContext";
import { convertStars } from "../../../api/cabinet";
import { requestPlayTicket } from "../../../api/playTicket";
import { Button, Card, Skeleton, plural } from "../../ui";
import "../../../styles/wallet.css";

/**
 * Кошелёк Рублокса — страница /app/wallet (порт WalletPage Маськи).
 *
 * Балансы алмазов и звёзд, покупка звёзд (эквайринг на Минкрафтии, перед
 * оплатой согласие по ЗоЗПП), конвертация звёзд в алмазы (5★ = 1💎,
 * односторонняя — алмазы в звёзды обменять НЕЛЬЗЯ), описания валют и
 * карточки экосистемы.
 *
 * Что исправлено при переводе на дизайн-систему:
 *  • JWT передавался в query-строке на домен школы — утечка в историю
 *    браузера, Referer и логи. Заменён на одноразовый play-ticket в hash;
 *  • склонение «Получить N алмаз…» было `n < 5 ? "а" : "ов"` — давало
 *    «21 алмазов» и «101 алмазов». Теперь общая функция plural() с тестом;
 *  • балансы `null` показывались прочерком навсегда, без загрузки и без
 *    объяснения, почему кнопка обмена выключена. Теперь скелетон;
 *  • кнопка «Конвертируем…» меняла ширину целиком → состояние loading;
 *  • три системы кнопок в одном файле (свои `.rb-wallet__btn`, чужие
 *    `.rb-up__modal-btn` из user-profile-page.css и голые <button>
 *    в степпере) → один <Button>. Импорт чужого CSS убран.
 */

const RATE = 5; // 5★ = 1 алмаз (курс на бэке, /star/convert)

// В кошельке балансы показываем ТОЧНО (1 543, не «1,5 тыс.») —
// это страница про деньги, сокращения тут вводят в заблуждение.
const exact = (n) => Number(n).toLocaleString("ru-RU");

export default function WalletScreen() {
  const { diamonds, stars, level, setDiamondsLocal, setStarsLocal } = useCabinet();
  const [convOpen, setConvOpen] = useState(false);
  const [convAmount, setConvAmount] = useState(RATE);
  const [convBusy, setConvBusy] = useState(false);
  const [convError, setConvError] = useState("");
  const [convDone, setConvDone] = useState(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyAgree, setBuyAgree] = useState(false);
  const [buyBusy, setBuyBusy] = useState(false);
  const [buyError, setBuyError] = useState("");

  /* null = балансы ещё не пришли. Отличать это от нуля обязательно:
     при нуле обмен невозможен по существу, при null — просто не знаем. */
  const loadingBalances = diamonds == null || stars == null;
  const maxConv = stars != null ? stars - (stars % RATE) : 0;

  const doConvert = async () => {
    if (convBusy) return;
    setConvBusy(true);
    setConvError("");
    try {
      const r = await convertStars(convAmount);
      if (r && r.ok === false) {
        setConvError(r.message || "Не удалось конвертировать");
        return;
      }
      if (typeof r.stars_remaining === "number") setStarsLocal(r.stars_remaining);
      if (typeof r.diamonds_total === "number") setDiamondsLocal(r.diamonds_total);
      /* Подтверждение обязательно. Раньше панель просто закрывалась, алмазы
         менялись где-то вверху страницы, и на медленной сети это выглядело
         как «ничего не произошло». */
      setConvDone(convAmount / RATE);
      setConvOpen(false);
      setTimeout(() => setConvDone(null), 4000);
    } catch (e) {
      setConvError(e?.message || "Не удалось конвертировать");
    } finally {
      setConvBusy(false);
    }
  };

  /* Покупка звёзд — эквайринг живёт на Минкрафтии (страница /stars).
     Аккаунт единый, поэтому надо перенести сессию на её домен.

     Раньше сюда подставлялся СЫРОЙ JWT в query-строке:
       /stars?token=<jwt>&from=rublox
     Так делать нельзя: query попадает в историю браузера, в заголовок
     Referer при загрузке любой сторонней картинки на странице и в логи
     nginx. Долгоживущий токен доступа утекал сразу в несколько мест.

     Теперь — одноразовый play-ticket в hash-части. Hash браузер на сервер
     не отправляет вовсе, тикет живёт секунды и сгорает при первом обмене.
     Тем же способом уже открываются плеер и Студия. */
  const proceedBuyStars = async () => {
    if (buyBusy) return;
    setBuyBusy(true);
    setBuyError("");
    try {
      const ticket = await requestPlayTicket();
      window.location.assign(
        `https://minecraftia-school.ru/stars?from=rublox#ticket=${encodeURIComponent(ticket)}`
      );
    } catch {
      // Молча уводить на страницу оплаты, где человек окажется гостем,
      // хуже, чем честно сказать об ошибке.
      setBuyError("Не получилось открыть оплату. Попробуй ещё раз.");
      setBuyBusy(false);
    }
  };

  const gain = convAmount / RATE;

  return (
    <div className="ui-page rb-wallet-page">
      <div className="rb-wallet-page__head">
        <span className="rb-wallet-page__head-ico">
          <CabIcon name="diamond" size={22} />
        </span>
        <div className="rb-wallet-page__head-text">
          <h1 className="rb-wallet-page__title">Кошелёк</h1>
          <div className="rb-wallet-page__sub">
            Алмазы и звёзды: балансы, покупка, обмен
          </div>
        </div>
      </div>

      <div className="rb-wallet__body">
        {/* ── Алмазы ── */}
        <div className="rb-wallet__card rb-wallet__card--diamonds">
          <div className="rb-wallet__balance">
            <CabIcon name="diamond" size={28} />
            {loadingBalances ? (
              <Skeleton w={120} h={34} />
            ) : (
              <span>{exact(diamonds)}</span>
            )}
          </div>
          <div className="rb-wallet__caption">Твой баланс алмазов</div>
          {level > 0 && <div className="rb-wallet__caption-sub">Уровень {level}</div>}
        </div>

        {/* ── Звёзды ── */}
        <div className="rb-wallet__card rb-wallet__card--stars">
          <div className="rb-wallet__balance">
            <CabIcon name="star" size={28} />
            {loadingBalances ? (
              <Skeleton w={100} h={34} />
            ) : (
              <span>{exact(stars)}</span>
            )}
          </div>
          <div className="rb-wallet__caption">Звёзды — премиум-валюта</div>
          <div className="rb-wallet__row">
            <Button
              variant="solid"
              className="rb-wallet__btn-buy"
              onClick={() => {
                setBuyAgree(false);
                setBuyError("");
                setBuyOpen(true);
              }}
            >
              Купить
            </Button>
            {/* Причина недоступности названа в title: раньше кнопка просто
                была серой, и человек не понимал, почему нельзя обменять. */}
            <Button
              variant="ghost"
              className="rb-wallet__btn-conv"
              disabled={loadingBalances || stars < RATE}
              title={
                loadingBalances
                  ? "Загружаем баланс"
                  : stars < RATE
                  ? `Нужно минимум ${RATE} звёзд`
                  : undefined
              }
              onClick={() => {
                setConvAmount(RATE);
                setConvError("");
                setConvOpen(true);
              }}
            >
              В алмазы
            </Button>
          </div>
        </div>

        {/* Подтверждение обмена */}
        {convDone != null && (
          <Card accent className="rb-wallet__done">
            <CabIcon name="check" size={19} />
            <span>
              Готово: получено {convDone} {plural(convDone, "алмаз", "алмаза", "алмазов")}.
            </span>
          </Card>
        )}

        {/* ── Конвертация ── */}
        {convOpen && (
          <div className="rb-wallet__sub">
            <div className="rb-wallet__sub-title">Конвертация</div>
            <div className="rb-wallet__sub-text">
              Курс: <b>{RATE} звёзд = 1 алмаз</b>. Конвертация необратима —
              алмазы обратно в звёзды не обмениваются.
            </div>
            <div className="rb-wallet__conv">
              <Button
                variant="ghost"
                iconOnly
                aria-label="Уменьшить"
                icon={<CabIcon name="minus" size={16} />}
                disabled={convBusy || convAmount <= RATE}
                onClick={() => setConvAmount((a) => Math.max(RATE, a - RATE))}
                className="rb-wallet__step"
              />
              <div className="rb-wallet__conv-val">
                <span className="rb-wallet__conv-stars">
                  <CabIcon name="star" size={16} /> {convAmount}
                </span>
                <span className="rb-wallet__conv-arrow">
                  → <CabIcon name="diamond" size={13} /> {gain}
                </span>
              </div>
              <Button
                variant="ghost"
                iconOnly
                aria-label="Увеличить"
                icon={<CabIcon name="plus" size={16} />}
                disabled={convBusy || convAmount + RATE > maxConv}
                title={
                  convAmount + RATE > maxConv ? "Это весь доступный баланс" : undefined
                }
                onClick={() => setConvAmount((a) => Math.min(maxConv, a + RATE))}
                className="rb-wallet__step"
              />
            </div>
            {convError && <div className="rb-wallet__error">{convError}</div>}
            <div className="rb-wallet__actions">
              <Button
                variant="primary"
                loading={convBusy}
                disabled={convAmount < RATE || convAmount > (stars || 0)}
                onClick={doConvert}
              >
                Получить {gain} {plural(gain, "алмаз", "алмаза", "алмазов")}
              </Button>
              <Button variant="ghost" disabled={convBusy} onClick={() => setConvOpen(false)}>
                Отмена
              </Button>
            </div>
          </div>
        )}

        {/* ── Подтверждение покупки звёзд (ЗоЗПП) ── */}
        {buyOpen && (
          <div className="rb-wallet__sub">
            <div className="rb-wallet__sub-title">Покупка звёзд</div>
            <div className="rb-wallet__sub-text">
              Звёзды — цифровой товар: они зачисляются сразу после оплаты.
              По закону после зачисления вернуть их уже нельзя, поэтому нужно
              подтвердить согласие.
            </div>
            <label className="rb-wallet__agree">
              <input
                type="checkbox"
                checked={buyAgree}
                onChange={(e) => setBuyAgree(e.target.checked)}
              />
              <span>
                Я подтверждаю, что мне исполнилось 18 лет либо покупка совершается
                с согласия и под контролем моего законного представителя, и согласен
                на немедленное предоставление звёзд. Я понимаю, что после их
                зачисления право на отказ утрачивается.
              </span>
            </label>
            {buyError && <div className="rb-wallet__error">{buyError}</div>}
            <div className="rb-wallet__actions">
              <Button
                variant="primary"
                disabled={!buyAgree}
                loading={buyBusy}
                onClick={proceedBuyStars}
              >
                Перейти к оплате
              </Button>
              <Button variant="ghost" disabled={buyBusy} onClick={() => setBuyOpen(false)}>
                Отмена
              </Button>
            </div>
          </div>
        )}

        {/* ── Что такое алмазы ── */}
        <div className="rb-wallet__label">Что такое алмазы</div>
        <div className="rb-wallet__info">
          <p>
            <b>Алмазы</b> — единая валюта всей экосистемы. Один аккаунт работает
            и в Рублоксе, и в школе Майнкрафтия, и в Маське — баланс общий.
          </p>
          <p>
            Заработать алмазы можно активностью: решай задачи в Майнкрафтии,
            публикуй игры и получай достижения в Рублоксе. За алмазы покупаются
            скины, рамки, обложка профиля, темы и подарки друзьям.
          </p>
        </div>

        {/* ── Что такое звёзды ── */}
        <div className="rb-wallet__label">Что такое звёзды</div>
        <div className="rb-wallet__info rb-wallet__info--stars">
          <p>
            <b>Звёзды</b> — премиум-валюта. Их покупают за рубли (отдельно от
            алмазов). За звёзды доступны особые рамки аватара и другие
            премиум-приколы — список будет расти.
          </p>
          <p>
            Звёзды можно обменять на алмазы ({RATE} звёзд = 1 алмаз).
            Обратно — алмазы в звёзды — обменять нельзя.
          </p>
        </div>

        {/* ── Заработать больше ── */}
        <div className="rb-wallet__label">Заработать больше алмазов</div>
        <a
          className="rb-wallet__eco"
          href="https://minecraftia-school.ru"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="rb-wallet__eco-ico rb-wallet__eco-ico--green">
            <CabIcon name="book" size={20} />
          </span>
          <span className="rb-wallet__eco-body">
            <span className="rb-wallet__eco-title">Майнкрафтия</span>
            <span className="rb-wallet__eco-sub">
              Онлайн-школа программирования. Решай задачи — получай алмазы.
            </span>
          </span>
          <CabIcon name="arrowRight" size={16} />
        </a>
        <a
          className="rb-wallet__eco"
          href="https://маська.рф"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="rb-wallet__eco-ico rb-wallet__eco-ico--pink">
            <CabIcon name="chat" size={20} />
          </span>
          <span className="rb-wallet__eco-body">
            <span className="rb-wallet__eco-title">Маська</span>
            <span className="rb-wallet__eco-sub">
              Мессенджер экосистемы. Общайся, дари подарки, играй.
            </span>
          </span>
          <CabIcon name="arrowRight" size={16} />
        </a>
      </div>
    </div>
  );
}
